import { CallSplitOutlined as CallSplitOutlinedIcon } from 'components/icons';
import { usePostHog } from '@posthog/react';
import PermissionButton from 'components/buttons/PermissionButton';
import ListCreateButton from 'components/buttons/ListCreateButton';
import EmployeeName from 'components/employee/EmployeeName';
import Loader from 'components/Loader';
import ReferralModal from 'components/modals/referrals/ReferralModal';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import {
    useAddReferralMutation,
    useDeleteReferralMutation,
    useLazyGetReferralsWithFiltersQuery,
    useUpdateReferralMutation,
} from 'store/api/referals';
import { NamedEntityMinDto } from 'types/other';
import { ReferralDto, ReferralListItem, ReferralTypeCode } from 'types/referrals';
import { errorAlert, successAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

const ReferralsTable = ({
    contactId,
    matterId,
    passiveFilter = '',
    dataTableClassName,
}: {
    contactId?: string;
    matterId?: string;
    passiveFilter?: string;
    dataTableClassName?: string;
}): JSX.Element => {
    const posthog = usePostHog();
    const { pathname, search } = useLocation();
    const [fetchData, { data, isFetching }] = useLazyGetReferralsWithFiltersQuery();
    const [addReferral] = useAddReferralMutation();
    const [updateReferral] = useUpdateReferralMutation();
    const [deleteReferral] = useDeleteReferralMutation();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['REFERRAL_TYPE', 'REFERRAL_OTHER_SOURCE_TYPE'],
    });
    const { openModal, closeModal } = useModal();

    const returnTo = useMemo(() => ({ returnTo: `${pathname + search}` }), [pathname, search]);

    const getOtherTypeCodeReferral = useCallback(
        (otherTypeCode: string | undefined, companyParty?: NamedEntityMinDto) => {
            const otherText = getClassifierText(classifiers['REFERRAL_OTHER_SOURCE_TYPE'], otherTypeCode);
            return (
                <div className="flex flex-col gap-2">
                    <div>{otherText}</div>
                    {companyParty?.id ? (
                        <PartyName
                            party={companyParty}
                            isLink
                            linkState={returnTo}
                            variant="list"
                            className="whitespace-nowrap"
                        />
                    ) : null}
                </div>
            );
        },
        [classifiers, returnTo]
    );

    const getContactReferral = useCallback(
        (companyParty?: NamedEntityMinDto, personParty?: NamedEntityMinDto) => {
            return (
                <div className="flex flex-col gap-2">
                    {companyParty?.id ? (
                        <PartyName
                            party={companyParty}
                            isLink
                            linkState={returnTo}
                            variant="list"
                            className="font-semibold whitespace-nowrap"
                        />
                    ) : null}
                    {personParty?.id ? (
                        <PartyName
                            party={personParty}
                            isLink
                            linkState={returnTo}
                            variant="list"
                            className="whitespace-normal"
                        />
                    ) : null}
                </div>
            );
        },
        [returnTo]
    );

    const handleReferralCreate = useCallback(
        async (referralDto: ReferralDto) => {
            try {
                await addReferral(referralDto).unwrap();
                posthog.capture('referral_added', {
                    referral_type: referralDto.referralTypeCode,
                    ...(referralDto.otherSourceTypeCode && { other_source_type: referralDto.otherSourceTypeCode }),
                });
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addReferral, posthog]
    );

    const handleReferralDelete = useCallback(
        async (referralId: string) => {
            try {
                await deleteReferral({ id: referralId }).unwrap();
                successAlert(t('referrals.referral-successfully-deleted'));
            } catch (e) {
                console.error('onDelete error: ', e);
                errorAlert(t('referrals.form.error-deleting-referral'));
            }
        },
        [deleteReferral]
    );

    const handleReferralUpdate = useCallback(
        async (referralDto: ReferralDto) => {
            try {
                await updateReferral(referralDto).unwrap();
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [updateReferral]
    );

    const openAddReferralModal = useCallback((): void => {
        openModal(
            <ReferralModal
                titleId={'referrals.form.add'}
                contactId={contactId}
                matterId={matterId}
                classifiers={classifiers}
                onChange={(value) => {
                    void handleReferralCreate(value);
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    }, [classifiers, contactId, matterId, openModal, closeModal, handleReferralCreate]);

    const openEditReferralModal = useCallback(
        (referralId: string): void => {
            openModal(
                <ReferralModal
                    titleId={'referrals.form.edit'}
                    referralId={referralId}
                    contactId={contactId}
                    matterId={matterId}
                    classifiers={classifiers}
                    onChange={(value) => {
                        void handleReferralUpdate(value);
                    }}
                    onClose={closeModal}
                    onDelete={(referralId) => {
                        void handleReferralDelete(referralId);
                    }}
                />,
                closeModal
            );
        },
        [classifiers, contactId, matterId, openModal, closeModal, handleReferralUpdate, handleReferralDelete]
    );

    const columns = useMemo<DataTableColumnDef<ReferralListItem>[]>(
        () => [
            {
                header: t('referrals.table.referral'),
                id: 'referral',
                enableSorting: false,
                meta: {
                    widthWeight: 2,
                    minPx: 180,
                },
                cell: ({ row }) => {
                    const { referralTypeCode, employee, companyParty, personParty, otherSourceTypeCode } = row.original;
                    switch (referralTypeCode) {
                        case ReferralTypeCode.EMPLOYEE:
                            return employee ? <EmployeeName entity={employee} /> : null;
                        case ReferralTypeCode.CONTACT:
                            return getContactReferral(companyParty, personParty);
                        case ReferralTypeCode.OTHER:
                            return getOtherTypeCodeReferral(otherSourceTypeCode, companyParty);
                        default:
                            return <div />;
                    }
                },
            },
            {
                header: t('referrals.table.source'),
                id: 'referralType',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ row }) => {
                    const { referralTypeCode } = row.original;
                    const referralTypeText = getClassifierText(classifiers['REFERRAL_TYPE'], referralTypeCode);
                    return <div className="text-fg-secondary">{referralTypeText}</div>;
                },
            },
            {
                header: t('referrals.table.referral-share-pct'),
                accessorKey: 'referralSharePct',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<number | undefined>();
                    return <div>{value ? `${value}%` : null}</div>;
                },
            },
            {
                header: t('referrals.table.notes'),
                accessorKey: 'notes',
                enableSorting: false,
                meta: {
                    widthWeight: 5.5,
                    minPx: 240,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    return <div>{value ? `${value}` : null}</div>;
                },
            },
            {
                id: 'button',
                enableSorting: false,
                meta: {
                    textAlign: 'right',
                    // Button column: sized to its content (37px button + cell padding) rather than
                    // taking a share of the flex pool.
                    fixedPx: 72,
                },
                cell: ({ cell }) => {
                    return (
                        <PermissionButton
                            permissions={['UPDATE.REFERRALS.ALL']}
                            variant="outlined"
                            size="extraSmall"
                            onClick={() => openEditReferralModal(cell.row.original.id as unknown as string)}
                        >
                            <FormattedMessage id="referrals.edit-referral" />
                        </PermissionButton>
                    );
                },
            },
        ],
        [classifiers, getContactReferral, getOtherTypeCodeReferral, openEditReferralModal]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'notes' }],
            passiveFilter: passiveFilter,
        }),
        [passiveFilter]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ReferralListItem>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="referrals"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="referrals.referrals"
            singularTextId="referrals.referral"
            noEntriesId="referrals.no-referrals-found"
            noEntriesBodyId="referrals.empty.body"
            noEntriesIcon={CallSplitOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            dataTableClassName={dataTableClassName}
            extraHeaderComponent={
                <ListCreateButton
                    permissions={['CREATE.REFERRALS']}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => openAddReferralModal()}
                    labelId="referrals.add-new-referral"
                    newLabelId="new-entity.referral"
                />
            }
            canRememberFilters={false}
        />
    );
};

export default ReferralsTable;
